import { useEffect, useRef, useState } from 'react';
import {
  readPhotoRecovery, retainPhotoRecovery, selectPhotoRecovery, discardPhotoRecovery,
  restorePhotoFields, restorePhotoRecovery, tabPhotoCopyId, legacyFailurePhotoCopyId, recordPhotoReceipt,
  samePhotoSnapshot, retireVerifiedPhotoCopies,
} from '@/utils/deliveryPhotoDraftPersistence';
import { withDeliveryCopyOperation } from '@/utils/deliveryCopyOperation';
import { hasLegacyDelivery } from '@/utils/deliveryOutbox';
import { getOrderDeliveryAttempt, getOrderDeliveryAmendment, getOrderDeliveryFailure } from '@/api/deliveries';
import { readDeliveryPhotoReceipt, readDeliveryPhotoAmendment } from '@/utils/deliveryPhotoSubmission';
import { readDeliveryFailureReceipt } from '@/utils/deliveryFailureSubmission';
import {
  getDeliveryWorkspace, hasDeliveryWorkspaceWork, acquireDeliveryAction, releaseDeliveryAction,
  getDeliveryAction, clearDeliveryWorkspace, createDeliveryWorkspace, updateDeliveryWorkspace,
  listDeliveryWorkspaces,
} from '@/utils/deliveryDraftStore';

let tabIdentity;
const sourceId = (work) => `${tabIdentity ||= crypto.randomUUID()}:${work.id}`;
const empty = { loading: false, busy: false, error: '', copies: [], canClose: true, canKeepBoth: false };

// TCA-26102306: review owns a frozen opening/copy selection, not submission
// authority. This coordinator never invokes an HTTP mutation or legacy replay.
export function useSavedPhotoRecovery({ enabled, opening, work, owns, isTabSaved, onSelected, onChanged, getToken }) {
  const [view, setView] = useState(null);
  const latest = useRef(null);
  latest.current = { enabled, opening, owns, isTabSaved, onSelected, onChanged, getToken };
  const owner = useRef(null);
  const retained = useRef(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; owner.current = null; };
  }, []);
  const currentWork = () => getDeliveryWorkspace(opening.principal, opening.orderId, opening.kind);
  const current = (ctx) => mounted.current && owner.current === ctx && latest.current.enabled
    && ctx.opening === opening && latest.current.opening === opening && latest.current.owns();
  const publish = (ctx, state) => {
    if (current(ctx)) { ctx.state = state; setView({ ctx, state }); }
  };
  const tabSnapshot = (value) => value && hasDeliveryWorkspaceWork(value) ? {
    id: sourceId(value), revision: value.revision, fields: value.fields,
    recovery: { attempt: value.attempt, checkpoint: value.checkpoint },
  } : null;

  const show = (ctx, saved, value) => {
    if (!current(ctx) || currentWork() !== value) return;
    const tab = ctx.savedTab === value || (latest.current.isTabSaved?.() && value?.photoDraftRevision === saved.active?.revision)
      ? null : tabSnapshot(value);
    const copies = [];
    const add = (record, id, source) => {
      if (!record) return;
      copies.push({ id, source, kind: opening.kind, revision: record.revision,
        fields: record.fields, attempt: record.recovery?.attempt || null, legacyAttempted: record.legacyAttempted === true });
    };
    add(saved.active, 'active', 'device');
    saved.alternatives.forEach((copy) => add(copy, copy.copyId, copy.source || 'device'));
    const alreadyRetained = tab && [saved.active, ...saved.alternatives].some((copy) => copy?.copyId === tabPhotoCopyId(tab));
    if (tab && !alreadyRetained) add({ fields: tab.fields, recovery: tab.recovery, revision: tab.revision }, 'tab', 'tab');
    const oldFailure = getDeliveryWorkspace(opening.principal, opening.orderId, 'failure');
    const failure = opening.kind === 'confirmation' && hasDeliveryWorkspaceWork(oldFailure) ? {
      id: sourceId(oldFailure), revision: oldFailure.revision, fields: oldFailure.fields, attempted: !!oldFailure.attempt,
    } : null;
    const failureRetained = failure && saved.alternatives.some((copy) => copy.copyId === legacyFailurePhotoCopyId(failure));
    if (failure && !failureRetained) copies.push({ id: 'legacy-failure-tab', source: 'legacy-failure', kind: 'failure',
      fields: { failureReason: failure.fields.reason, failureDetails: failure.fields.details, photos: [] }, legacyAttempted: failure.attempted });
    const otherWork = listDeliveryWorkspaces(opening.principal, opening.orderId)
      .some((candidate) => candidate.kind !== opening.kind && candidate !== oldFailure && hasDeliveryWorkspaceWork(candidate));
    const unresolved = copies.filter((copy) => copy.attempt && copy.attempt.status !== 'accepted');
    const held = otherWork || !!failure || copies.some((copy) => copy.legacyAttempted) || saved.resolution?.legacyFailureHeld;
    ctx.saved = saved; ctx.work = value; ctx.tab = tab; ctx.failure = failure; ctx.failureWork = oldFailure;
    publish(ctx, { ...empty, copies: copies.map((copy) => ({ ...copy,
      canSelect: !ctx.legacy && !held && copy.source !== 'legacy-failure' && (!unresolved.length || (copy.attempt?.captureId
        && unresolved.every((item) => item.attempt.kind === copy.attempt.kind && item.attempt.captureId === copy.attempt.captureId))),
      canDiscard: !copy.legacyAttempted && !['tab', 'legacy-failure-tab'].includes(copy.id)
        && !getDeliveryAction(value) && (!tab || alreadyRetained)
        && (copy.attempt?.kind === 'confirmation' || copy.attempt?.status === 'accepted' || (!otherWork && !copy.attempt && !unresolved.length)),
      canDiscardTab: !!tab && !value.attempt && !value.checkpoint && !value.savedMediaRemoval
        && (copy.id === 'tab' || (copy.id === 'active' ? saved.active?.copyId : copy.id) === tabPhotoCopyId(tab)),
      canCheckReceipt: !!copy.attempt?.captureId && copy.id !== 'tab',
    })), canKeepBoth: (!!tab && !alreadyRetained) || (!!failure && !failureRetained),
    message: ctx.legacy ? 'An older saved copy also exists for this order. Nothing will be sent from this screen.'
      : held ? 'Other saved work also exists for this order. Nothing will be sent from this screen.'
      : unresolved.length ? 'We couldn’t confirm this delivery. Your work is saved on this device.'
      : 'Choose a draft to continue, or delete a copy you no longer need.' });
  };

  const refresh = async (ctx = owner.current, checkOnOpen = false) => {
    if (!ctx || !current(ctx) || ctx.busy) return;
    const request = {}; ctx.request = request;
    const value = currentWork();
    publish(ctx, { ...empty, loading: true });
    try {
      const [saved, legacy] = await Promise.all([readPhotoRecovery(opening.principal, opening.orderId, opening.kind), hasLegacyDelivery(opening.orderId)]);
      if (!current(ctx) || ctx.request !== request) return;
      if (currentWork() !== value) throw new Error('Tab work changed. Refresh and review the copies again.');
      ctx.savedTab = value && value.photoDraftRevision === saved.active?.revision && await samePhotoSnapshot(value, saved.active) ? value : null;
      if (!current(ctx) || ctx.request !== request || currentWork() !== value) return;
      ctx.legacy = legacy;
      show(ctx, saved, value);
      if (checkOnOpen) {
        const checked = new Set();
        for (const copy of [...ctx.state.copies]) {
          if (!current(ctx)) break;
          if (copy.attempt?.kind !== 'confirmation' || !copy.canCheckReceipt || checked.has(copy.attempt.captureId)) continue;
          checked.add(copy.attempt.captureId);
          const fresh = ctx.state.copies.find((item) => item.id === copy.id);
          if (fresh) await checkReceipt(fresh);
        }
      }
    } catch (error) {
      if (current(ctx) && ctx.request === request) publish(ctx, { ...empty, error: error.message });
    }
  };
  const open = () => {
    if (latest.current.opening !== opening || !enabled || !owns() || getDeliveryAction(currentWork())) return;
    const ctx = { opening, state: empty, busy: false };
    owner.current = ctx; void refresh(ctx, true);
  };

  const mutate = async (action, copy) => {
    const ctx = owner.current;
    if (!ctx || !current(ctx) || ctx.busy || ctx.state.loading || ctx.state.error || !ctx.saved) return;
    if (copy && (!ctx.state.copies.includes(copy) || !(action === 'select' ? copy.canSelect
      : action === 'discard-tab' ? copy.canDiscardTab : copy.canDiscard))) return;
    if (action === 'retain' && !ctx.state.canKeepBoth) return;
    if (currentWork() !== ctx.work) {
      publish(ctx, { ...empty, error: 'Tab work changed. Refresh before choosing a copy.' }); return;
    }
    const value = ctx.work;
    const siblings = listDeliveryWorkspaces(opening.principal, opening.orderId).filter((candidate) => candidate.kind !== opening.kind);
    if (action === 'select' && siblings.some(hasDeliveryWorkspaceWork)) {
      publish(ctx, { ...empty, error: 'Other-operation work changed. Refresh before choosing a copy.' }); return;
    }
    // Forms already own a workspace. A future list entry may need an untouched
    // shell, but must not bypass the same per-order action owner.
    if (!value) { publish(ctx, { ...empty, error: 'Reopen the delivery form to review this copy.' }); return; }
    const lease = acquireDeliveryAction(value, `photo-recovery-${action}`);
    if (!lease) return;
    ctx.busy = true;
    publish(ctx, { ...ctx.state, busy: true });
    const isCurrent = () => {
      const now = listDeliveryWorkspaces(opening.principal, opening.orderId).filter((candidate) => candidate.kind !== opening.kind);
      return current(ctx) && currentWork() === value && getDeliveryAction(value) === lease
        && now.length === siblings.length && now.every((candidate) => siblings.includes(candidate));
    };
    try {
      await withDeliveryCopyOperation(opening.orderId, async () => {
        if (!isCurrent()) throw new Error('The opening changed. Nothing was selected.');
        if (action === 'discard-tab') {
          // Separate consent retires only the exact reviewed, wholly unsent
          // in-memory copy. Durable disposal never implies this local action.
          if (!ctx.tab || value.attempt || value.checkpoint || value.savedMediaRemoval
            || !clearDeliveryWorkspace(value, lease)) throw new Error('Tab custody changed. Reopen recovery.');
          retained.current = null; owner.current = null; setView(null);
          latest.current.onSelected?.({ action: 'discard', kind: opening.kind });
          return;
        }
        const options = {
          expectedActiveRevision: ctx.saved.active?.revision ?? null,
          expectedResolutionRevision: ctx.saved.resolution?.revision ?? null,
          isCurrent, ...(ctx.tab ? { tab: ctx.tab } : {}), ...(action === 'retain' && ctx.failure ? { legacyFailure: ctx.failure } : {}), ...(copy ? { copyId: copy.id } : {}),
          ...(action === 'discard' && copy?.attempt?.kind === 'confirmation' ? { allowUnverified: true } : {}),
        };
        if (action === 'select' && await hasLegacyDelivery(opening.orderId)) throw new Error('Older saved work overlaps this order. Nothing was selected for sending.');
        const operation = action === 'retain' ? retainPhotoRecovery : action === 'select' ? selectPhotoRecovery : discardPhotoRecovery;
        const saved = await operation(opening.principal, opening.orderId, opening.kind, options);
        if (!isCurrent()) return; // Committed state is recovered by fresh read, not replay.
        if (action === 'retain') {
          retained.current = { opening, work: value, failureWork: ctx.failure ? ctx.failureWork : null };
          releaseDeliveryAction(lease);
          show(ctx, saved, value);
        } else if (action === 'select') {
          // Allocate previews before retiring tab custody; a failure leaves the
          // old workspace intact and the committed selection available on read.
          const recovery = restorePhotoRecovery(saved.active.recovery);
          const fields = restorePhotoFields(saved.active.fields);
          if (!clearDeliveryWorkspace(value, lease)) throw new Error('Tab custody changed. Reopen recovery.');
          const replacement = createDeliveryWorkspace(opening.principal, opening.orderId, opening.kind, fields);
          releaseDeliveryAction(lease);
          const selectedWork = updateDeliveryWorkspace(replacement, { ...recovery, photoDraftRevision: saved.active.revision });
          retained.current = { opening, work: selectedWork, failureWork: null }; owner.current = null; setView(null);
          latest.current.onSelected?.({ action: 'select', kind: opening.kind });
        } else {
          latest.current.onChanged?.();
          if (ctx.tab && copy.id === tabPhotoCopyId(ctx.tab)) retained.current = null;
          if (ctx.failure && copy.id === legacyFailurePhotoCopyId(ctx.failure)) {
            releaseDeliveryAction(lease);
            if (getDeliveryWorkspace(opening.principal, opening.orderId, 'failure') === ctx.failureWork) clearDeliveryWorkspace(ctx.failureWork);
          }
          // Deleting a durable copy never implies discarding a different tab
          // revision. Clear tab custody only for this known saved active copy.
          if (copy.id === 'active' && !ctx.tab && value.photoDraftRevision === copy.revision) {
            clearDeliveryWorkspace(value, lease);
            owner.current = null; setView(null);
            latest.current.onSelected?.({ action: 'discard', kind: opening.kind });
          } else {
            releaseDeliveryAction(lease);
            show(ctx, saved, value);
          }
        }
      }, { requireCrossContext: true });
    } catch (error) {
      if (current(ctx)) publish(ctx, { ...empty, error: `${error.message} Refresh to read the actual saved state; keep this tab open.` });
    } finally {
      ctx.busy = false; releaseDeliveryAction(lease);
      if (current(ctx)) publish(ctx, { ...ctx.state, busy: false });
    }
  };
  const checkReceipt = async (copy) => {
    const ctx = owner.current;
    if (!ctx || !current(ctx) || ctx.busy || ctx.state.loading || !ctx.state.copies.includes(copy) || !copy.canCheckReceipt) return;
    const token = latest.current.getToken?.();
    if (!token) { publish(ctx, { ...ctx.state, message: 'Connect with your current account to check the submission result. Local work is retained.' }); return; }
    let value = currentWork();
    if (value !== ctx.work) { await refresh(ctx); return; }
    const lease = acquireDeliveryAction(value, 'review-saved-receipt');
    if (!lease) return;
    ctx.busy = true; publish(ctx, { ...ctx.state, busy: true });
    const isCurrent = () => current(ctx) && currentWork() === value && getDeliveryAction(value) === lease;
    let verifiedDelivery = false;
    try {
      await withDeliveryCopyOperation(opening.orderId, async () => {
        if (!isCurrent()) return;
        const attempt = copy.attempt;
        const lookup = attempt.kind === 'failure' ? getOrderDeliveryFailure : attempt.kind === 'amendment' ? getOrderDeliveryAmendment : getOrderDeliveryAttempt;
        const result = await lookup(opening.orderId, attempt.captureId, { token });
        if (!isCurrent()) return;
        const outcome = attempt.kind === 'failure' ? { state: readDeliveryFailureReceipt(attempt, opening.orderId, result), attempt }
          : attempt.kind === 'amendment' ? readDeliveryPhotoAmendment(attempt, opening.orderId, result) : readDeliveryPhotoReceipt(attempt, opening.orderId, result);
        let saved = await recordPhotoReceipt(opening.principal, opening.orderId, opening.kind, {
          copyId: copy.id, expectedCopyRevision: copy.revision, expectedActiveRevision: ctx.saved.active?.revision ?? null,
          expectedResolutionRevision: ctx.saved.resolution?.revision ?? null, outcome, isCurrent,
        });
        if (!isCurrent()) return;
        // Recording a receipt advances durable state, but does not create new driver work.
        if (!ctx.tab && copy.id === 'active' && value.photoDraftRevision === copy.revision && saved.active) {
          value = updateDeliveryWorkspace(value, { ...restorePhotoRecovery(saved.active.recovery), photoDraftRevision: saved.active.revision }, lease);
          ctx.savedTab = value;
        }
        // Keep the acknowledged revision retryable even if the following cleanup fails.
        show(ctx, saved, value); publish(ctx, { ...ctx.state, busy: true });
        if (outcome.state === 'accepted' && attempt.kind === 'confirmation') {
          verifiedDelivery = true;
          const verified = copy.id === 'active' ? saved.active : saved.alternatives.find((item) => item.copyId === copy.id);
          // A different running-tab edit must remain even when a durable attempt completed.
          saved = await retireVerifiedPhotoCopies(opening.principal, opening.orderId, opening.kind, {
            copyId: copy.id, expectedCopyRevision: verified.revision, isCurrent,
          });
          if (!isCurrent()) return;
          latest.current.onChanged?.();
          if (!ctx.tab && value.photoDraftRevision && !saved.active) {
            clearDeliveryWorkspace(value, lease); owner.current = null; setView(null);
            latest.current.onSelected?.({ action: 'cleaned', kind: opening.kind });
            return;
          }
        }
        releaseDeliveryAction(lease); show(ctx, saved, value);
        publish(ctx, { ...ctx.state, message: outcome.state === 'accepted'
          ? attempt.kind === 'confirmation' ? 'Delivery confirmed. Its completed saved copies have been removed.'
            : 'Report received. Photos that were not uploaded are still on this device.'
          : 'We couldn’t confirm this delivery. Your work is saved on this device.' });
      }, { requireCrossContext: true });
    } catch (error) {
      if (current(ctx)) publish(ctx, { ...ctx.state, message: verifiedDelivery
        ? 'Delivery confirmed, but its saved copy could not be removed. Check again to retry cleanup.'
        : 'We couldn’t confirm this delivery. Your work is saved on this device.' });
    } finally {
      ctx.busy = false; releaseDeliveryAction(lease);
      if (current(ctx)) {
        const message = ctx.state.message;
        if (currentWork() === value && ctx.saved) show(ctx, ctx.saved, value);
        publish(ctx, { ...ctx.state, message, busy: false });
      }
    }
  };
  const visible = enabled && view && current(view.ctx);
  return {
    open, isOpen: !!visible, state: visible ? view.state : empty,
    savedCurrent: enabled && retained.current?.opening === opening && retained.current.work === work,
    isSafelyRetained: (candidate) => !!candidate && enabled && retained.current?.opening === opening
      && (retained.current.work === candidate || retained.current.failureWork === candidate),
    refresh: () => refresh(), keepBoth: () => mutate('retain'), select: (copy) => mutate('select', copy),
    discard: (copy) => mutate('discard', copy),
    discardTab: (copy) => mutate('discard-tab', copy),
    checkReceipt,
    close: () => { if (owner.current && current(owner.current) && !owner.current.busy) { owner.current = null; setView(null); } },
  };
}
